import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { TenantProfileViewStore } from "../../store";
import { TenantProfileView } from "../view/TenantProfileView";
import { TenantProfileUpdateStore } from "../../store";
import { TenantProfileUpdate } from "../update/TenantProfileUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type TenantProfileModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const TenantProfileModalContent: React.FC<TenantProfileModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new TenantProfileUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new TenantProfileViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <TenantProfileView
            tenantProfileViewStore={storeInfo.store as TenantProfileViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <TenantProfileUpdate
            tenantProfileUpdateStore={
              storeInfo.store as TenantProfileUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
